import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { hexToRgbA } from 'utils/colors';

export const StyledTooltip = styled(motion.div)<{ zoom: number }>`
  z-index: 10000000;
  min-width: ${({ zoom }) => 196 * zoom}px;
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 6px 20px
    ${({ theme }) => hexToRgbA(theme.color.pickledBluewood, 0.14)};
  padding: ${({ zoom }) => `${6 * zoom}px ${4 * zoom}px`};
  border-radius: ${({ zoom }) => 10 * zoom}px;
  overflow: hidden;

  & > svg {
    path {
      fill: ${({ theme }) => theme.color.white};
    }
  }
`;

export const StyledMenuBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const StyledCategory = styled.div<{ zoom: number }>`
  padding: ${({ zoom }) =>
    `${6 * zoom}px ${12 * zoom}px ${2 * zoom}px ${12 * zoom}px`};
  font-size: ${({ zoom }) => 11 * zoom}px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.baliHai};
  line-height: 1.3;
  user-select: none;
`;

export const StyledDivider = styled.div<{ zoom: number }>`
  height: 1px;
  margin: ${({ zoom }) => `${4 * zoom}px ${10 * zoom}px`};
  background-color: ${({ theme }) => theme.color.porcelain};
`;

export const StyledOption = styled.div<{
  zoom: number;
  $danger?: boolean;
}>`
  padding: ${({ zoom }) => `0 ${10 * zoom}px`};
  margin: ${({ zoom }) => `0 ${2 * zoom}px`};
  border-radius: ${({ zoom }) => 6 * zoom}px;
  min-height: ${({ zoom }) => 34 * zoom}px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme, $danger }) =>
    $danger ? theme.color.bittersweet : theme.color.shark};
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: ${({ zoom }) => 13 * zoom}px;
    height: ${({ zoom }) => 13 * zoom}px;
    flex-shrink: 0;
    margin-right: ${({ zoom }) => 9 * zoom}px;

    path {
      fill: ${({ theme, $danger }) =>
        $danger ? theme.color.bittersweet : theme.color.shark};
      transition: fill 0.2s ease;
    }
  }

  label {
    display: flex;
    font-size: ${({ zoom }) => 13 * zoom}px;
    font-weight: 500;
    line-height: 1.3;
    cursor: pointer;
  }

  &:hover {
    ${({ theme, $danger }) =>
      $danger
        ? css`
            color: ${theme.color.white};
            background-color: ${theme.color.bittersweet};
            box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;

            svg path {
              fill: ${theme.color.white};
            }
          `
        : css`
            color: ${theme.color.white};
            background-color: ${theme.color.brightTurquoise};
            box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;

            svg path {
              fill: ${theme.color.white};
            }
          `}
  }
`;

export const StyledThreeDotsTrigger = styled.div<{ zoom: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ zoom }) => 17 * zoom}px;
  height: ${({ zoom }) => 17 * zoom}px;
  flex-shrink: 0;
`;

export const StyledThreeDots = styled.button<{ zoom: number }>`
  box-sizing: border-box;
  width: ${({ zoom }) => 17 * zoom}px;
  height: ${({ zoom }) => 17 * zoom}px;
  padding: 0;
  margin: 0;
  border: ${({ zoom, theme }) => `${2 * zoom}px solid ${theme.color.white}`};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease, transform 0.2s ease;

  /* Square child so rotate(90deg) keeps the glyph optically centered */
  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ zoom }) => 10 * zoom}px;
    height: ${({ zoom }) => 10 * zoom}px;
    transform: rotate(90deg);
    transform-origin: center center;
    flex-shrink: 0;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;

    path {
      fill: ${({ theme }) => theme.color.white};
    }
  }

  &:hover {
    background-color: ${({ theme }) => hexToRgbA(theme.color.white, 0.18)};
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.brightTurquoise};
  }
`;

export const StyledLoaderWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => hexToRgbA(theme.color.white, 0.72)};
  border-radius: inherit;
  z-index: 1;
`;
